/* Anno per anno, strategia contro mercato.
 *
 * Non c'è in `componenti/Grafici.tsx` e serve solo qui, quindi vive qui.
 *
 * Due scelte che contano:
 *
 * 1. La barra della strategia è colorata dal SUO segno — verde se quell'anno ha
 *    guadagnato, rossa se ha perso — e il mercato resta grigio. Il prototipo
 *    usava il teal del marchio per la strategia, ma il teal è troppo vicino al
 *    verde del profitto e le fondamenta lo riservano al logo. Colorare per
 *    segno dice una cosa in più e non ne toglie nessuna: la legenda spiega chi
 *    è chi con la forma, non solo col colore.
 *
 * 2. L'altezza è compressa con la RADICE del valore assoluto, col segno
 *    riportato dopo. I valori vanno da −80% a +500% e oltre: su scala lineare
 *    tutti gli anni normali si schiacciano contro lo zero e sembra che la
 *    strategia abbia lavorato una volta sola. La radice è la stessa
 *    compressione con cui si gradua il velo, e usarne una sola in tutto il
 *    grafico evita che altezza e sfondo raccontino la scala in due modi.
 *
 * 3. Il grafico sta SOPRA l'elenco testuale della stessa cosa, non al suo
 *    posto: l'elenco è la lettura precisa e ordinata, il grafico è la forma —
 *    dove stanno gli anni buoni, quanto è profondo il peggiore, se il vantaggio
 *    è di un anno solo o di tutti. Sono due domande diverse sugli stessi
 *    numeri.
 */

import { pct } from '../../lib/formato';
import type { RendimentoAnnuale } from '../../api/tipi';

/** Radice del valore assoluto, segno riportato: comprime senza annullare i piccoli. */
function comprimi(v: number): number {
  return Math.sign(v) * Math.sqrt(Math.abs(v));
}

/**
 * Il rendimento dell'anno, oppure `null` se non è stato misurato.
 *
 * Qui c'era `?? 0` su ogni lettura, sette volte, e le conseguenze erano le
 * peggiori possibili per un ripiego: la barra veniva disegnata a filo dello
 * zero come se l'anno fosse stato piatto; veniva colorata di VERDE, perché
 * `0 >= 0`; il fumetto scriveva `+0,0%` come misura; e soprattutto l'anno
 * risultava NON di ribasso, quindi spariva in silenzio dagli anni che formano
 * «nei ribassi». Un anno non misurato non è un anno andato in pari: la barra
 * non si disegna e l'anno si segna come non misurato.
 */
function misurato(v: number | null | undefined): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export function GraficoAnnuale({
  anni,
  altezza = 230,
}: {
  anni: RendimentoAnnuale[];
  altezza?: number;
}) {
  if (anni.length === 0) return null;

  const L = 720;
  const padSopra = 16;
  /* Sotto ogni anno ci stanno tre righe: l'anno, il rendimento della strategia e quello del
     mercato. I numeri sono SCRITTI e non affidati al passaggio del mouse: su telefono quel
     gesto non esiste, e questo grafico si guarda soprattutto da telefono. */
  const padSotto = 62;
  const utile = altezza - padSopra - padSotto;

  /* La scala si costruisce sui soli anni misurati: uno zero inventato
     tirerebbe dentro un estremo che non esiste. */
  const valori = anni
    .flatMap((a) => [misurato(a.change), misurato(a.marketChange)])
    .filter((v): v is number => v !== null)
    .map(comprimi);
  const massimo = Math.max(1, ...valori.map(Math.abs));

  /* Il vantaggio piu' grande della serie: e' il metro con cui si gradua il velo di ogni anno. */
  const vantaggioMassimo = Math.max(
    0,
    ...anni.map((a) => {
      const c = misurato(a.change);
      const m = misurato(a.marketChange);
      return c == null || m == null ? 0 : Math.abs(c - m);
    }),
  );

  const passo = L / anni.length;
  const largh = Math.min(22, passo / 3.2);
  /* Lo zero non sta a metà se tutti i valori hanno lo stesso segno: sprecherebbe
     metà grafico. */
  const minimo = Math.min(0, ...valori);
  const escursione = massimo - minimo || 1;
  const Y = (v: number) => padSopra + (1 - (v - minimo) / escursione) * utile;
  const yZero = Y(0);

  return (
    <div className="sb-scorri-x">
      <svg
        viewBox={`0 0 ${L} ${altezza}`}
        width="100%"
        height={altezza}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', minWidth: Math.max(320, anni.length * 46) }}
        role="img"
        aria-label="Rendimento della strategia e del mercato, anno per anno"
      >
        <line x1={0} x2={L} y1={yZero} y2={yZero} stroke="var(--sb-bordo-forte)" strokeWidth={1} />

        {anni.map((a, i) => {
          const cx = i * passo + passo / 2;
          const cambio = misurato(a.change);
          const mercato = misurato(a.marketChange);
          const s = cambio == null ? null : comprimi(cambio);
          const m = mercato == null ? null : comprimi(mercato);
          /* Il velo dietro l'anno dice UNA cosa sola: se la strategia ha battuto il mercato,
             e di quanto.
             
             Prima diceva se il mercato era sceso, che è la domanda sbagliata. Un anno con il
             mercato a −60% e la strategia a +12% si accendeva di rosso, come se fosse andata
             male: era andata benissimo, ed è proprio l'anno che si cerca guardando questo
             grafico. Ora quell'anno è verde, e lo è anche un anno in cui perdono entrambi ma la
             strategia perde meno.
             
             L'intensità è proporzionale al vantaggio, compressa con la radice e normalizzata sul
             vantaggio più grande dell'anno migliore: senza comprimere, un anno a +244 punti
             renderebbe invisibili tutti gli altri. È una gradazione per confrontare a colpo
             d'occhio, non per misurare -- il numero esatto sta nella differenza in punti.
             
             Formula e fondo sono gli stessi dell'elenco testuale qui sotto: lo stesso anno deve
             risultare tinto allo stesso modo nelle due forme, altrimenti confrontarle
             costringerebbe a chiedersi quale delle due sta dicendo la verità. Ogni anno
             confrontabile è tinto, anche di poco: «ha battuto o no» è un fatto binario, e un
             anno senza velo si leggerebbe come non confrontato. */
          const incompleto = cambio == null || mercato == null;
          const vantaggio = cambio != null && mercato != null ? cambio - mercato : null;
          const forza =
            vantaggio == null || vantaggioMassimo === 0
              ? 0
              : Math.min(1, Math.sqrt(Math.abs(vantaggio)) / Math.sqrt(vantaggioMassimo));

          return (
            <g key={a.year}>
              {vantaggio != null && (
                <rect
                  x={i * passo + 2}
                  y={padSopra - 8}
                  width={passo - 4}
                  height={utile + 16}
                  fill={vantaggio >= 0 ? 'var(--sb-guadagno)' : 'var(--sb-perdita)'}
                  opacity={0.05 + forza * 0.16}
                  rx={4}
                />
              )}

              {s != null && cambio != null && (
                <rect
                  x={cx - largh - 2}
                  y={Math.min(Y(s), yZero)}
                  width={largh}
                  height={Math.max(1.5, Math.abs(Y(s) - yZero))}
                  rx={2}
                  fill={cambio >= 0 ? 'var(--sb-guadagno)' : 'var(--sb-perdita)'}
                />
              )}

              {m != null && mercato != null && (
                <rect
                  x={cx + 2}
                  y={Math.min(Y(m), yZero)}
                  width={largh}
                  height={Math.max(1.5, Math.abs(Y(m) - yZero))}
                  rx={2}
                  fill="var(--sb-bordo-marcato)"
                />
              )}

              <text
                x={cx}
                y={altezza - 48}
                textAnchor="middle"
                fontSize={10}
                fontFamily="var(--sb-font-numeri)"
                fill={incompleto ? 'var(--sb-cautela-testo)' : 'var(--sb-testo-tenue)'}
              >
                {a.year}
              </text>

              {/* I due numeri, scritti e sempre presenti. Un grafico di barre senza le cifre
                  costringe a stimare a occhio proprio la cosa che si e' venuti a leggere, e la
                  scala qui e' compressa: a occhio si sbaglierebbe di molto. */}
              <text
                x={cx}
                y={altezza - 32}
                textAnchor="middle"
                fontSize={11}
                fontFamily="var(--sb-font-numeri)"
                fontWeight={600}
                fill={
                  cambio == null
                    ? 'var(--sb-cautela-testo)'
                    : cambio >= 0
                      ? 'var(--sb-guadagno)'
                      : 'var(--sb-perdita)'
                }
              >
                {cambio == null ? 'n/d' : pct(cambio, 0)}
              </text>
              <text
                x={cx}
                y={altezza - 19}
                textAnchor="middle"
                fontSize={10}
                fontFamily="var(--sb-font-numeri)"
                fill="var(--sb-testo-medio)"
              >
                {mercato == null ? 'n/d' : pct(mercato, 0)}
              </text>

              {/* La terza riga e' il confronto, che e' il motivo per cui il grafico esiste:
                  quanti punti la strategia ha guadagnato o perso rispetto al mercato. */}
              {vantaggio != null && (
                <text
                  x={cx}
                  y={altezza - 5}
                  textAnchor="middle"
                  fontSize={10}
                  fontFamily="var(--sb-font-numeri)"
                  fontWeight={600}
                  fill={vantaggio >= 0 ? 'var(--sb-guadagno)' : 'var(--sb-perdita)'}
                >
                  {vantaggio >= 0 ? '+' : '−'}
                  {Math.abs(Math.round(vantaggio)).toLocaleString('it-IT')} pt
                </text>
              )}
              {incompleto && (
                <text
                  x={cx}
                  y={altezza - 5}
                  textAnchor="middle"
                  fontSize={9}
                  fontFamily="var(--sb-font-numeri)"
                  fill="var(--sb-cautela-testo)"
                >
                  non misurato
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
